import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";
import { Loader2 } from "lucide-react";

const buttonVariants = cva(
  "inline-flex items-center justify-center text-sm capitalize font-medium transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",

  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary hover:bg-opacity-90",
        brand:
          "bg-cw-blue text-cw-blue-foreground hover:bg-cw-blue hover:bg-opacity-90 hover:text-cw-blue-foreground hover:text-opacity-90",
        hero: "bg-[#ECFBF5] text-[#00728B] hover:bg-[#ECFBF5] hover:bg-opacity-80",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive hover:bg-opacity-95",
        "destructive-secondary": "text-destructive hover:opacity-90",
        outline:
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground bg-transparent",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary hover:bg-opacity-80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        sketch: "bg-zinc-100 hover:brightness-105",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "px-4 py-2 gap-2",
        sm: "px-2 py-0.5 text-xs gap-1",
        md: "px-3 py-1.5 text-xs gap-1",
        lg: "px-8 py-3 gap-4",
        xl: "px-10 py-4 text-xl gap-4",
        icon: "h-9 w-9",
      },
      rounded: {
        default: "rounded-md",
        true: "rounded-full",
      },
      disabled: {
        default: "",
        true: "bg-zinc-100 text-zinc-300 pointer-events-none",
      },
      loading: {
        default: "",
        true: "pointer-events-none",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      rounded: "default",
    },
  }
);

export interface ButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "disabled" | "loading"
    >,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  disabled?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      disabled,
      loading,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            disabled,
            loading,
            rounded,
            className,
          })
        )}
        ref={ref}
        {...props}
      >
        {loading ? (
          <span className="flex items-center justify-center min-h-[20px]">
            <Loader2 width={16} height={16} className="animate-spin" />
          </span>
        ) : (
          props.children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
